import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { HandbookListItem } from "@/types/handbook";

interface HandbookParentPickerProps {
  /** 현재 선택된 상위 항목 _id 배열. */
  value: string[];
  onChange: (ids: string[]) => void;
  /** 전체 handbook 항목 (트리 트래버스 + 검색에 쓰임) */
  items: HandbookListItem[];
  /** 편집 중인 handbook id (자기 자신 + 자손 제외) */
  excludeId?: string;
  disabled?: boolean;
  /** 최대 선택 수 (백엔드는 10) */
  maxSelections?: number;
}

interface TreeNode {
  item: HandbookListItem;
  depth: number;
}

function flattenTree(
  items: HandbookListItem[],
  excludeId?: string,
): TreeNode[] {
  // parentIds 기반 트리 빌드 → 사전순 DFS 평탄화.
  // 같은 노드가 여러 부모 아래에 나타날 수 있으나 picker UI에서는 첫 등장 한 번만 노출
  // (선택 결과는 _id 기반이라 어디서 클릭해도 동일).
  const byParent = new Map<string, HandbookListItem[]>();
  const ROOT_KEY = "__root__";
  for (const item of items) {
    const keys = item.parentIds.length === 0 ? [ROOT_KEY] : item.parentIds;
    for (const key of keys) {
      if (!byParent.has(key)) byParent.set(key, []);
      byParent.get(key)!.push(item);
    }
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => a.title.localeCompare(b.title));
  }

  const exclude = new Set<string>();
  if (excludeId) {
    // 자기 자신 + 자손 모두 차단.
    exclude.add(excludeId);
    const stack = [excludeId];
    while (stack.length > 0) {
      const cur = stack.pop()!;
      const kids = byParent.get(cur) ?? [];
      for (const k of kids) {
        if (exclude.has(k._id)) continue;
        exclude.add(k._id);
        stack.push(k._id);
      }
    }
  }

  const result: TreeNode[] = [];
  const seen = new Set<string>();
  const visit = (parentKey: string, depth: number) => {
    const children = byParent.get(parentKey) ?? [];
    for (const child of children) {
      if (seen.has(child._id) || exclude.has(child._id)) continue;
      seen.add(child._id);
      result.push({ item: child, depth });
      visit(child._id, depth + 1);
    }
  };
  visit(ROOT_KEY, 0);
  return result;
}

export function HandbookParentPicker({
  value,
  onChange,
  items,
  excludeId,
  disabled,
  maxSelections = 10,
}: HandbookParentPickerProps) {
  const [open, setOpen] = useState(false);

  const flat = useMemo(() => flattenTree(items, excludeId), [items, excludeId]);
  const itemById = useMemo(() => {
    const m = new Map<string, HandbookListItem>();
    for (const it of items) m.set(it._id, it);
    return m;
  }, [items]);
  const selectedSet = useMemo(() => new Set(value), [value]);

  const toggle = (id: string) => {
    if (selectedSet.has(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      if (value.length >= maxSelections) return;
      onChange([...value, id]);
    }
  };

  const removeOne = (id: string) => {
    onChange(value.filter((v) => v !== id));
  };

  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground">
        상위 항목 (선택, 여러 개 가능)
      </label>

      {/* 선택된 부모 뱃지 리스트 */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((id) => {
            const item = itemById.get(id);
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 rounded-md border bg-muted/40 px-2 py-0.5 text-xs"
              >
                {item ? item.title : `(${id.slice(-6)})`}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => removeOne(id)}
                    className="opacity-60 hover:opacity-100"
                    aria-label="제거"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between font-normal"
          >
            <span className="text-muted-foreground">
              {value.length === 0
                ? "최상위 (부모 없음)"
                : `${value.length}개 선택됨 — 추가/해제`}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0"
          align="start"
        >
          <Command>
            <CommandInput placeholder="제목으로 검색..." />
            <CommandList>
              <CommandEmpty>일치하는 항목 없음</CommandEmpty>
              <CommandGroup>
                {flat.map(({ item, depth }) => {
                  const checked = selectedSet.has(item._id);
                  return (
                    <CommandItem
                      key={item._id}
                      value={`${item.title} ${item.slug}`}
                      onSelect={() => toggle(item._id)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          checked ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span
                        style={{ paddingLeft: `${depth * 12}px` }}
                        className="truncate"
                      >
                        {item.title}
                      </span>
                      <code className="ml-auto text-[10px] text-muted-foreground">
                        {item.slug}
                      </code>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
